import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { tWithKeyAttr as t } from "../../../../i18nHelper";
import i18n from "../../../../i18n";
import styles from "../EditAppointmentModal/EditAppointmentModal.module.css";
import seriesStyles from "./CreateAppointmentModal.module.css";
import {
  useValidateAppointmentChangeMutation,
  useCreateAppointmentFastMutation,
  useGetAllMetaTablesQuery,
  useGetProtocolsByCompanyQuery,
} from "../../../../services/api/utils.api";
import { useLazyGetUsersByCompanyIdWithRecommendsQuery, useUpdateTherapistsUsersConnectionsMutation, useLazyGetStaffDevicesQuery } from "../../../../services/api/user.api";
import { useAppointmentsContext } from "../../context/AppointmentsContext";
import { useAppSelector } from "../../../../services/hooks";
import { adminSelector } from "../../../../services/selectors/user.selectors";
import { appointmentsProgramsDataSelector } from "../../../../services/selectors/utils.selectors";
import { timeToMinutes } from "../../../../utils/utils";
import {
  IAppointmentProgramProtocol,
  ISelectedProtocol,
  TProtocolFrequencyType,
} from "../../../../services/types/utils.types";
import TreatmentPlanModal from "../TreatmentPlanModal/TreatmentPlanModal";
import DeviceConfigurationModal from "../DeviceConfigurationModal/DeviceConfigurationModal";
import { AppointmentDevice, StaffDevice } from "../../../../services/types/device.types";
import { getDeviceSummaryLabel } from "../../../../utils/deviceConfiguration";

/** API user row: skip StatusId === 0 in appointment dropdowns */
const notInactiveUser = (u: any) => (u.StatusId ?? u.statusId ?? 1) !== 0;

type UserOption = { id: number; guid: string; fullName: string; phone?: string };

interface ICreateAppointmentModalProps {
  onClose: () => void;
  onSuccess: () => void;
  initialDate: string;
  initialTime?: string;
  therapistId?: number;
}

interface IConflict {
  type: string;
  message: string;
  severity: "warning" | "info";
}

interface ITherapistAvailability {
  dayId: number;
  startTime: string;
  endTime: string;
}

const CreateAppointmentModal: React.FC<ICreateAppointmentModalProps> = ({
  onClose,
  onSuccess,
  initialDate,
  initialTime = "09:00",
  therapistId: initialTherapistId,
}) => {
  const isRtl = i18n.language === "he";
  const admin = useAppSelector(adminSelector);
  const { therapistsList } = useAppointmentsContext();
  const appointmentsProgramsData = useAppSelector(appointmentsProgramsDataSelector);

  // API hooks
  const [validateChange] = useValidateAppointmentChangeMutation();
  const [postAppointment, { isLoading: isCreating }] = useCreateAppointmentFastMutation();
  const [searchPatients, { isFetching: isSearchingPatients }] = useLazyGetUsersByCompanyIdWithRecommendsQuery();
  const [updateTherapistsUsersConnections, { isLoading: isLinking }] = useUpdateTherapistsUsersConnectionsMutation();
  const [getStaffDevices, { isFetching: isFetchingDevices }] = useLazyGetStaffDevicesQuery();

  useGetAllMetaTablesQuery(admin.companyId, { skip: !admin.companyId });
  const { data: protocolsData = [] } = useGetProtocolsByCompanyQuery(
    { companyId: admin.companyId },
    { skip: !admin.companyId }
  );

  // Form state
  const [date, setDate] = useState(initialDate);
  const [startTime, setStartTime] = useState(initialTime);
  const [endTime, setEndTime] = useState(() => {
    const [h, m] = initialTime.split(":").map(Number);
    const dateObj = new Date();
    dateObj.setHours(h, m + 50);
    return dateObj.toTimeString().slice(0, 5);
  });
  const [therapistId, setTherapistId] = useState(initialTherapistId || 0);
  const [therapistGuid, setTherapistGuid] = useState("");
  const [patientId, setPatientId] = useState(0);
  const [patientGuid, setPatientGuid] = useState("");
  const [selectedPatientName, setSelectedPatientName] = useState("");
  const [mediaId, setMediaId] = useState(1);
  const [comments, setComments] = useState("");
  const [repeatCount, setRepeatCount] = useState(1);
  const [appointmentType, setAppointmentType] = useState<'treatment' | 'other'>('treatment');

  // Series settings state
  const [isPlanPanelOpen, setIsPlanPanelOpen] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);
  const [appointmentsDurationsId, setAppointmentsDurationsId] = useState(0);
  const [appointmentsSequenceId, setAppointmentsSequenceId] = useState(0);
  const [selectedProtocols, setSelectedProtocols] = useState<ISelectedProtocol[]>([]);
  const [protocolsValid, setProtocolsValid] = useState(true);
  const [durationManuallySet, setDurationManuallySet] = useState(false);
  // Plan configured flag + section-2 details collected from TreatmentPlanModal
  const [planConfigured, setPlanConfigured] = useState(false);
  const [planDetails, setPlanDetails] = useState<{
    treatmentPlanName?: string;
    roleId?: number;
    genderId?: number;
    migzarId?: number;
    treatmentRoomId?: number;
    fundingSourcesId?: number;
    treatmentTypesId?: number;
  }>({});

  // UI state
  const [patientSearch, setPatientSearch] = useState("");
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const [allPatients, setAllPatients] = useState<any[]>([]);
  const [showTherapistDropdown, setShowTherapistDropdown] = useState(false);
  const [therapistSearch, setTherapistSearch] = useState("");
  const [showMediaDropdown, setShowMediaDropdown] = useState(false);
  
  // Validation state
  const [conflicts, setConflicts] = useState<IConflict[]>([]);
  const [therapistAvailability, setTherapistAvailability] = useState<ITherapistAvailability[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const [associationError, setAssociationError] = useState("");
  const [deviceError, setDeviceError] = useState("");
  const [showDeviceModal, setShowDeviceModal] = useState(false);
  const [staffDevices, setStaffDevices] = useState<StaffDevice[]>([]);
  const [appointmentDevice, setAppointmentDevice] = useState<AppointmentDevice | null>(null);

  // Linked options (filtered by TherapistsUsers association)
  const [patientsForTherapist, setPatientsForTherapist] = useState<UserOption[]>([]);
  const [therapistsForPatient, setTherapistsForPatient] = useState<UserOption[]>([]);
  const [isFetchingLinked, setIsFetchingLinked] = useState(false);

  // Refs
  const patientDropdownRef = useRef<HTMLDivElement>(null);
  const therapistDropdownRef = useRef<HTMLDivElement>(null);
  const mediaDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setAssociationError("");
    setDeviceError("");
  }, [patientId, therapistId]);

  const selectedTherapist = useMemo(
    () => therapistsList.find((t) => t.id === therapistId),
    [therapistsList, therapistId],
  );

  const therapistRequiresDevice = Boolean(selectedTherapist?.hasDevices);

  const openDeviceConfiguration = useCallback(
    async (guidOverride?: string) => {
      const guid =
        guidOverride ||
        therapistGuid ||
        therapistsList.find((t) => t.id === therapistId)?.guid ||
        "";

      if (!guid) {
        setDeviceError(t("SOMETHING_WENT_WRONG_TRY_AGAIN_LATER"));
        return;
      }

      if (!therapistGuid) {
        setTherapistGuid(guid);
      }

      try {
        const result = await getStaffDevices(guid).unwrap();
        const devices = result.devices ?? [];
        setStaffDevices(devices);
        if (devices.length === 0) {
          setDeviceError(t("DEVICE_LIST_EMPTY"));
          return;
        }
        setDeviceError("");
        setShowDeviceModal(true);
      } catch {
        setDeviceError(t("SOMETHING_WENT_WRONG_TRY_AGAIN_LATER"));
      }
    },
    [getStaffDevices, therapistGuid, therapistId, therapistsList],
  );

  const handleTherapistSelect = useCallback(
    async (id: number, guid: string) => {
      const resolvedGuid = guid || therapistsList.find((t) => t.id === id)?.guid || "";

      setTherapistId(id);
      setTherapistGuid(resolvedGuid);
      setAppointmentDevice(null);
      setDeviceError("");
      setShowTherapistDropdown(false);

      const therapist = therapistsList.find((t) => t.id === id);
      if (!therapist?.hasDevices) return;

      await openDeviceConfiguration(resolvedGuid);
    },
    [openDeviceConfiguration, therapistsList],
  );

  // Initial therapist GUID lookup
  useEffect(() => {
    if (initialTherapistId && therapistsList.length > 0) {
      const therapist = therapistsList.find(t => t.id === initialTherapistId);
      if (therapist) {
        setTherapistGuid(therapist.guid || "");
      }
    }
  }, [initialTherapistId, therapistsList]);

  // Initial Patient Load
  useEffect(() => {
    if (admin.companyId) {
      const fetchAllPatients = async () => {
        try {
          const result = await searchPatients({
            companyIds: [admin.companyId],
            isMinimal: true,
            roleIds: [1],
          }).unwrap();

          const reports = (result as any).Users || [];
          const users = reports.map((r: any) => r.Users).filter((u: any) => u && notInactiveUser(u));
          
          const mappedUsers = users.map((u: any) => ({
              id: u.Id,
              guid: u.Guid,
              fullName: u.FullName || `${u.FirstName} ${u.LastName}`,
              phone: u.Phone
          }));

          setAllPatients(mappedUsers);
        } catch (err) {
          console.error(err);
        }
      };
      fetchAllPatients();
    }
  }, [admin.companyId, searchPatients]);

  // Fetch patients linked to selected therapist (for treatment type only)
  useEffect(() => {
    if (appointmentType !== "treatment" || !therapistId || !therapistGuid || !admin.companyId) {
      setPatientsForTherapist([]);
      return;
    }
    const therapist = therapistsList.find((t) => t.id === therapistId);
    if (!therapist?.guid) return;

    const fetchLinked = async () => {
      setIsFetchingLinked(true);
      try {
        const result = await searchPatients({
          companyIds: [admin.companyId],
          isMinimal: true,
          roleIds: [1],
          therapistGuids: [therapist.guid || ""],
        }).unwrap();
        const reports = ((result as any).Users || []) as { Users?: any }[];
        const users = reports.map((r) => r.Users).filter((u: any) => u && notInactiveUser(u));
        const mapped: UserOption[] = users.map((u: any) => ({
          id: u.Id,
          guid: u.Guid ?? "",
          fullName: u.FullName || `${u.FirstName || ""} ${u.LastName || ""}`.trim(),
          phone: u.Phone,
        }));
        setPatientsForTherapist(mapped);
      } catch {
        setPatientsForTherapist([]);
      } finally {
        setIsFetchingLinked(false);
      }
    };
    fetchLinked();
  }, [appointmentType, therapistId, therapistGuid, admin.companyId, searchPatients, therapistsList]);

  // Fetch therapists linked to selected patient (for treatment type only)
  useEffect(() => {
    if (appointmentType !== "treatment" || !patientId || !patientGuid || !admin.companyId) {
      setTherapistsForPatient([]);
      return;
    }

    const fetchLinked = async () => {
      setIsFetchingLinked(true);
      try {
        const result = await searchPatients({
          companyIds: [admin.companyId],
          isMinimal: true,
          roleIds: [-1],
          userGuids: [patientGuid],
        }).unwrap();
        const reports = ((result as any).Users || []) as { Users?: any }[];
        const users = reports.map((r) => r.Users).filter((u: any) => u && notInactiveUser(u));
        const mapped: UserOption[] = users.map((u: any) => ({
          id: u.Id,
          guid: u.Guid ?? "",
          fullName: u.FullName || `${u.FirstName || ""} ${u.LastName || ""}`.trim(),
        }));
        setTherapistsForPatient(mapped);
      } catch {
        setTherapistsForPatient([]);
      } finally {
        setIsFetchingLinked(false);
      }
    };
    fetchLinked();
  }, [appointmentType, patientId, patientGuid, admin.companyId, searchPatients]);

  // Derived state (Memos) - always show all, sort linked first with marker
  const therapistOptions = useMemo(() => {
    const linkedIds = new Set(therapistsForPatient.map((t) => t.id));
    const allTherapists = therapistsList
      .filter((t) => t.isActive || t.id === therapistId)
      .map((t) => ({
        id: t.id,
        guid: t.guid || "",
        fullName: t.name,
        isLinked: patientId ? linkedIds.has(t.id) : false,
        hasDevices: t.hasDevices ?? false,
      }));
    const search = therapistSearch.toLowerCase();
    const filtered = search
      ? allTherapists.filter((t) => t.fullName.toLowerCase().includes(search))
      : allTherapists;
    return [...filtered].sort((a, b) => (b.isLinked ? 1 : 0) - (a.isLinked ? 1 : 0));
  }, [therapistsList, therapistsForPatient, patientId, therapistId, therapistSearch]);

  const patientOptions = useMemo(() => {
    const linkedIds = new Set(patientsForTherapist.map((p) => p.id));
    const allWithLinked = allPatients.map((p) => ({
      ...p,
      isLinked: therapistId ? linkedIds.has(p.id) : false,
    }));
    const query = patientSearch.toLowerCase();
    const filtered = query
      ? allWithLinked.filter((p) => p.fullName.toLowerCase().includes(query) || p.phone?.includes(query))
      : allWithLinked;
    return [...filtered].sort((a, b) => (b.isLinked ? 1 : 0) - (a.isLinked ? 1 : 0));
  }, [allPatients, patientsForTherapist, therapistId, patientSearch]);

  const isPatientTherapistLinked = useMemo(() => {
    if (!patientId || !therapistId) return true;
    return (
      patientsForTherapist.some((p) => p.id === patientId) ||
      therapistsForPatient.some((t) => t.id === therapistId)
    );
  }, [patientId, therapistId, patientsForTherapist, therapistsForPatient]);

  const handleLinkTherapistPatient = async () => {
    if (!therapistGuid || !patientGuid) return;
    try {
      await updateTherapistsUsersConnections({
        type: 1,
        body: { therapistsGuids: [therapistGuid], usersGuids: [patientGuid] },
      }).unwrap();
      const patientOpt = allPatients.find((p) => p.id === patientId);
      const therapistOpt = therapistsList.find((t) => t.id === therapistId);
      if (patientOpt && !patientsForTherapist.some((p) => p.id === patientId)) {
        setPatientsForTherapist((prev) => [...prev, patientOpt]);
      }
      if (therapistOpt && !therapistsForPatient.some((t) => t.id === therapistId)) {
        setTherapistsForPatient((prev) => [
          ...prev,
          { id: therapistOpt.id, guid: therapistOpt.guid || "", fullName: therapistOpt.name },
        ]);
      }
      setAssociationError("");
    } catch (err) {
      console.error("Failed to link therapist-patient:", err);
    }
  };

  const selectedTherapistName = useMemo(() => {
    const fromList = therapistsList.find((t) => t.id === therapistId)?.name;
    if (fromList) return fromList;
    return therapistsForPatient.find((t) => t.id === therapistId)?.fullName || "";
  }, [therapistsList, therapistsForPatient, therapistId]);

  // Validation Effect
  useEffect(() => {
    const validate = async () => {
      if (!therapistId || !date || !startTime || !endTime || appointmentType !== 'treatment') return;
      
      setIsValidating(true);
      try {
        const result = await validateChange({
          appointmentId: 0,
          therapistsGuid: therapistGuid || undefined,
          usersGuid: patientGuid || undefined,
          date: date,
          startTime: startTime,
          endTime: endTime,
        }).unwrap();

        setConflicts(result.conflicts || []);
        setTherapistAvailability(result.therapistAvailability || []);
      } catch (error) {
        console.error("Validation error:", error);
      } finally {
        setIsValidating(false);
      }
    };

    const timeout = setTimeout(validate, 500);
    return () => clearTimeout(timeout);
  }, [date, startTime, endTime, therapistId, therapistGuid, patientGuid, appointmentType, validateChange]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (therapistDropdownRef.current && !therapistDropdownRef.current.contains(event.target as Node)) {
        setShowTherapistDropdown(false);
      }
      if (mediaDropdownRef.current && !mediaDropdownRef.current.contains(event.target as Node)) {
        setShowMediaDropdown(false);
      }
      if (patientDropdownRef.current && !patientDropdownRef.current.contains(event.target as Node)) {
        setShowPatientDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isStartBeforeEnd = timeToMinutes(startTime) < timeToMinutes(endTime);

  const durationOptions = useMemo(
    () =>
      appointmentsProgramsData.AppointmentsDurations.map((d) => ({
        value: Number(d.value ?? d.Id),
        label: `${d.Minutes ?? d.label} ${t("MINUTES_SUFFIX")}`,
      })),
    [appointmentsProgramsData.AppointmentsDurations]
  );

  const sequenceOptions = useMemo(
    () =>
      appointmentsProgramsData.AppointmentsSequence.map((s) => ({
        value: Number(s.value ?? s.Id),
        label: String(s.label ?? s.CountInPeriod),
      })),
    [appointmentsProgramsData.AppointmentsSequence]
  );

  const sessionDurationMinutes = useMemo(() => {
    return timeToMinutes(endTime) - timeToMinutes(startTime);
  }, [startTime, endTime]);

  // Plan is valid once the modal was saved (planConfigured), the frequency is set,
  // and the appointment has a positive duration (derived from start/end times).
  const seriesSettingsValid =
    repeatCount <= 1 ||
    (planConfigured && appointmentsSequenceId > 0 && sessionDurationMinutes > 0);

  const handleRepeatCountChange = useCallback(
    (newCount: number) => {
      setRepeatCount((previousCount) => {
        if (previousCount === 1 && newCount > 1 && !hasAutoOpened) {
          setIsPlanPanelOpen(true);
          setHasAutoOpened(true);
        }
        return newCount;
      });
    },
    [hasAutoOpened]
  );

  const handleDurationChange = useCallback((id: number) => {
    setDurationManuallySet(true);
    setAppointmentsDurationsId(id);
  }, []);

  const buildProtocolsPayload = useCallback((): IAppointmentProgramProtocol[] => {
    return selectedProtocols.map((p) => {
      switch (p.frequencyType) {
        case "sequential":
          return {
            protocolId: p.protocolId,
            frequencyType: 2 as TProtocolFrequencyType,
            startAtSession: 1,
            everyNSessions: p.everyNSessions,
          };
        case "afterFirst":
          return {
            protocolId: p.protocolId,
            frequencyType: 1 as TProtocolFrequencyType,
            startAtSession: 2,
            everyNSessions: null,
          };
        case "afterFirstOnce":
          return {
            protocolId: p.protocolId,
            frequencyType: 3 as TProtocolFrequencyType,
            startAtSession: 2,
            everyNSessions: null,
          };
        default:
          return {
            protocolId: p.protocolId,
            frequencyType: 1 as TProtocolFrequencyType,
            startAtSession: 1,
            everyNSessions: null,
          };
      }
    });
  }, [selectedProtocols]);

  // Auto-select duration matching start/end time difference
  useEffect(() => {
    if (durationManuallySet || durationOptions.length === 0 || sessionDurationMinutes <= 0) {
      return;
    }

    const closest = durationOptions.reduce((best, opt) => {
      const minutes = parseInt(String(opt.label), 10);
      if (isNaN(minutes)) return best;
      const bestMinutes = best ? parseInt(String(best.label), 10) : Infinity;
      return Math.abs(minutes - sessionDurationMinutes) <
        Math.abs(bestMinutes - sessionDurationMinutes)
        ? opt
        : best;
    }, null as (typeof durationOptions)[0] | null);

    if (closest) {
      setAppointmentsDurationsId(Number(closest.value));
    }
  }, [durationOptions, sessionDurationMinutes, durationManuallySet]);

  const handleCreate = async () => {
    if (appointmentType === 'treatment' && (!patientId || !therapistId)) return;
    if (appointmentType === 'other' && !therapistId) return;
    if (therapistRequiresDevice && !appointmentDevice) {
      setDeviceError(t("DEVICE_SESSION_REQUIRED"));
      return;
    }

    setAssociationError("");
    setDeviceError("");

    try {
      await postAppointment({
        appointmentDate: date,
        startTime: startTime,
        endTime: endTime,
        therapistsId: therapistId,
        therapistsGuid: therapistGuid,
        usersId: appointmentType === 'treatment' ? patientId : null,
        usersGuid: appointmentType === 'treatment' ? patientGuid : null,
        mediaId: appointmentType === 'treatment' ? mediaId : null,
        comments: comments,
        companyId: admin.companyId || 0,
        countOfAppointments: repeatCount,
        appointmentsDurationsId: repeatCount > 1 ? appointmentsDurationsId : null,
        // Duration (minutes) drives the program's duration; harmless for a plain single.
        appointmentsDurationMinutes: sessionDurationMinutes,
        // Frequency only applies to a multi-session series.
        appointmentsSequenceId: repeatCount > 1 ? appointmentsSequenceId : null,
        protocols: appointmentType === 'treatment' ? buildProtocolsPayload() : [],
        // Plan details from TreatmentPlanModal. Sent for single too — the backend only
        // spins up a program when at least one of these (or a protocol) is present.
        treatmentPlanName: planDetails.treatmentPlanName,
        roleId: planDetails.roleId,
        genderId: planDetails.genderId,
        migzarId: planDetails.migzarId,
        treatmentRoomId: planDetails.treatmentRoomId,
        fundingSourcesId: planDetails.fundingSourcesId,
        treatmentTypesId: planDetails.treatmentTypesId,
        device: appointmentDevice ?? undefined,
      }).unwrap();
      onSuccess();
    } catch (error: unknown) {
      const err = error as { data?: { code?: string; message?: string; desc?: string } };
      if (err?.data?.code === "THERAPIST_PATIENT_NOT_LINKED") {
        setAssociationError(err.data?.message || t("APPOINTMENTS_THERAPIST_PATIENT_NOT_LINKED"));
      } else if (err?.data?.message || err?.data?.desc) {
        setDeviceError(err.data?.message || err.data?.desc || t("SOMETHING_WENT_WRONG_TRY_AGAIN_LATER"));
      } else {
        console.error("Failed to create appointment:", error);
      }
    }
  };

  const formatDate = (dateStr: string): string => {
    try {
        const date = new Date(dateStr);
        return date.toISOString().split('T')[0];
    } catch {
        return dateStr;
    }
  };

  const dayNames = [
    t("SUNDAY"), t("MONDAY"), t("TUESDAY"), t("WEDNESDAY"), t("THURSDAY"), t("FRIDAY"), t("SATURDAY")
  ];
  const getDayName = (dayId: number) => dayNames[dayId - 1] || "";

  const mediaTypes = [
    { id: 1, name: t("MEDIA_CHAT") },
    { id: 2, name: t("MEDIA_VIDEO") },
    { id: 3, name: t("MEDIA_PHONE") },
    { id: 4, name: t("MEDIA_CLINIC") },
  ];

  return (
    <div className={styles.modalOverlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.modal} dir={isRtl ? "rtl" : "ltr"}>
        <div className={styles.modalHeader}>
          <div className={styles.modalTitle}>
            <span>➕</span>
            <span>{t("APPOINTMENTS_NEW_TITLE")}</span>
          </div>
          <button className={styles.closeBtn} onClick={onClose}>×</button>
        </div>

        <div className={styles.modalBody}>
          {/* Type Toggle */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '20px',
            backgroundColor: '#f3f4f6',
            borderRadius: '8px',
            padding: '4px',
            gap: '4px'
          }}>
            <button
              onClick={() => setAppointmentType('treatment')}
              style={{
                flex: 1,
                padding: '8px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: appointmentType === 'treatment' ? '#fff' : 'transparent',
                boxShadow: appointmentType === 'treatment' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                cursor: 'pointer',
                fontWeight: appointmentType === 'treatment' ? 600 : 400,
                color: appointmentType === 'treatment' ? '#8b5cf6' : '#6b7280',
                transition: 'all 0.2s'
              }}
            >
              {t("TREATMENT") || "טיפול"}
            </button>
            <button
              onClick={() => setAppointmentType('other')}
              style={{
                flex: 1,
                padding: '8px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: appointmentType === 'other' ? '#fff' : 'transparent',
                boxShadow: appointmentType === 'other' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                cursor: 'pointer',
                fontWeight: appointmentType === 'other' ? 600 : 400,
                color: appointmentType === 'other' ? '#8b5cf6' : '#6b7280',
                transition: 'all 0.2s'
              }}
            >
              {t("OTHER") || "אחר"}
            </button>
          </div>

          {appointmentType === 'treatment' && (
            /* Patient Selection */
            <div className={styles.formGroup} ref={patientDropdownRef}>
              <label className={styles.label}>
                <span className={styles.labelIcon}>👤</span>
                {t("APPOINTMENTS_PATIENT")}
              </label>
              <div className={styles.customSelect}>
                <div
                  className={`${styles.customSelectTrigger} ${showPatientDropdown ? styles.customSelectActive : ''}`}
                  onClick={() => setShowPatientDropdown(!showPatientDropdown)}
                >
                  <span className={styles.customSelectValue}>
                    {patientId ? `👤 ${selectedPatientName}` : t("SELECT_PATIENT")}
                  </span>
                  <span className={styles.customSelectArrow}>▼</span>
                </div>
                {showPatientDropdown && (
                  <div className={styles.customSelectDropdown}>
                    <div className={styles.searchContainer}>
                      <input
                        type="text"
                        className={styles.input}
                        style={{ minHeight: '36px', padding: '8px 12px', fontSize: '14px' }}
                        placeholder={t("SEARCH_PLACEHOLDER")}
                        value={patientSearch}
                        onChange={(e) => setPatientSearch(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        autoFocus
                      />
                    </div>
                    <div className={styles.dropdownOptions}>
                      {patientId > 0 && (
                        <div
                          className={styles.dropdownOption}
                          onClick={() => {
                            setPatientId(0);
                            setPatientGuid("");
                            setSelectedPatientName("");
                            setShowPatientDropdown(false);
                          }}
                          style={{ borderBottom: "1px solid #e5e7eb" }}
                        >
                          <span className={styles.optionText} style={{ color: "#6b7280", fontStyle: "italic" }}>
                            ✕ {t("CLEAN_THE_SELECTION") || t("CLEAR") || "נקה בחירה"}
                          </span>
                        </div>
                      )}
                      {(isSearchingPatients || (therapistId && isFetchingLinked)) ? (
                        <div className={styles.dropdownOption} style={{ justifyContent: 'center', cursor: 'default' }}>
                          <span className={styles.optionText}>{t("LOADING")}...</span>
                        </div>
                      ) : patientOptions.length > 0 ? (
                        patientOptions.map((p) => (
                          <div
                            key={p.id}
                            className={`${styles.dropdownOption} ${patientId === p.id ? styles.dropdownOptionSelected : ''}`}
                            onClick={() => {
                              setPatientId(p.id);
                              setPatientGuid(p.guid);
                              setSelectedPatientName(p.fullName);
                              setShowPatientDropdown(false);
                            }}
                          >
                            <span className={styles.optionText}>
                              {p.isLinked && <span style={{ marginInlineEnd: 6, color: '#22c55e' }}>✓</span>}
                              {p.fullName}
                            </span>
                            {patientId === p.id && <span className={styles.checkmark}>✓</span>}
                          </div>
                        ))
                      ) : (
                        <div className={styles.dropdownOption} style={{ justifyContent: 'center', cursor: 'default' }}>
                          <span className={styles.optionText}>{t("NO_RESULTS")}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className={styles.formGroup}>
            <label className={styles.label}>
              <span className={styles.labelIcon}>📅</span>
              {t("APPOINTMENTS_DATE")}
            </label>
            <input
              type="date"
              className={styles.input}
              value={formatDate(date)}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>
                <span className={styles.labelIcon}>⏰</span>
                {t("APPOINTMENTS_START_TIME")}
              </label>
              <input
                type="time"
                className={styles.input}
                value={startTime}
                style={(!isStartBeforeEnd) ? { borderColor: '#ef4444', backgroundColor: '#fef2f2' } : {}}
                onChange={(e) => {
                  setStartTime(e.target.value);
                  const [h, m] = e.target.value.split(":").map(Number);
                  if (!isNaN(h)) {
                    const d = new Date();
                    d.setHours(h, m + 50);
                    setEndTime(d.toTimeString().slice(0, 5));
                  }
                }}
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>{t("APPOINTMENTS_END_TIME")}</label>
              <input
                type="time"
                className={styles.input}
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
              />
            </div>
            {!isStartBeforeEnd && startTime && endTime && (
              <div style={{ fontSize: '12px', color: '#dc2626', marginTop: '-10px', marginBottom: '10px' }}>
                  {t("START_TIME_BEFORE_END_TIME_ERROR") || "שעת הסיום חייבת להיות אחרי שעת ההתחלה"}
              </div>
          )}
          </div>

          {appointmentType === 'treatment' && (
            <>
            <div className={styles.formGroup}>
              <label className={styles.label}>
                <span className={styles.labelIcon}>🔢</span>
                {t("NUMBER_OF_APPOINTMENTS") || "מספר פגישות"}
              </label>
              <div className={seriesStyles.countButtons}>
                {[10, 9, 8, 7, 6, 5, 4, 3, 2, 1].map((num) => (
                  <button
                    key={num}
                    onClick={() => handleRepeatCountChange(num)}
                    type="button"
                    className={`${seriesStyles.countButton} ${
                      repeatCount === num ? seriesStyles.countButtonSelected : ""
                    }`}
                  >
                    {num}
                  </button>
                ))}
              </div>
              <div className={seriesStyles.countHint}>
                {t("SESSION_COUNT_HINT")}
              </div>
            </div>

            <div className={styles.formGroup}>
              <button
                type="button"
                className={seriesStyles.configurePlanBtn}
                onClick={() => setShowPlanModal(true)}
              >
                <span className={seriesStyles.configurePlanIcon}>🗓️</span>
                <span className={seriesStyles.configurePlanText}>
                  {repeatCount > 1
                    ? t("CONFIGURE_TREATMENT_PLAN") || "הגדר תוכנית טיפול"
                    : t("CONFIGURE_PROTOCOLS") || "הגדר פרוטוקולים"}
                </span>
                {(() => {
                  // Multi = full plan (requires frequency). Single = protocols only.
                  if (repeatCount > 1) {
                    if (planConfigured && appointmentsSequenceId > 0) {
                      const parts = [
                        sequenceOptions.find((o) => o.value === appointmentsSequenceId)?.label,
                        `${sessionDurationMinutes} ${t("MINUTES_SUFFIX") || "דקות"}`,
                        selectedProtocols.length > 0
                          ? `${selectedProtocols.length} ${t("PROTOCOLS") || "פרוטוקולים"}`
                          : null,
                      ].filter(Boolean);
                      return (
                        <span
                          className={seriesStyles.configurePlanSummary}
                          style={{ display: "flex", flexDirection: "column", gap: "2px", textAlign: "start" }}
                        >
                          {planDetails.treatmentPlanName && (
                            <strong>{planDetails.treatmentPlanName}</strong>
                          )}
                          <span>{parts.join(" | ")}</span>
                        </span>
                      );
                    }
                    return (
                      <span className={seriesStyles.configurePlanRequired}>
                        {t("REQUIRED") || "חובה"}
                      </span>
                    );
                  }

                  // Single: show protocol count when set, otherwise "optional".
                  return selectedProtocols.length > 0 ? (
                    <span className={seriesStyles.configurePlanSummary}>
                      {`${selectedProtocols.length} ${t("PROTOCOLS") || "פרוטוקולים"}`}
                    </span>
                  ) : (
                    <span className={seriesStyles.configurePlanSummary}>
                      {t("OPTIONAL") || "אופציונלי"}
                    </span>
                  );
                })()}
              </button>
            </div>

            {showPlanModal && (
              <TreatmentPlanModal
                companyId={admin.companyId}
                repeatCount={repeatCount}
                appointmentsDurationsId={appointmentsDurationsId}
                appointmentsSequenceId={appointmentsSequenceId}
                calculatedDurationMinutes={sessionDurationMinutes}
                selectedProtocols={selectedProtocols}
                durationOptions={durationOptions}
                sequenceOptions={sequenceOptions}
                protocols={protocolsData}
                roleOptions={appointmentsProgramsData.Roles}
                genderOptions={appointmentsProgramsData.Genders}
                migzarOptions={appointmentsProgramsData.Migzars}
                treatmentRoomOptions={appointmentsProgramsData.TreatmentRoomsList}
                fundingSourcesOptions={appointmentsProgramsData.FundingSources}
                treatmentTypesOptions={appointmentsProgramsData.TreatmentTypes}
                mediaLabel={mediaTypes.find((m) => m.id === mediaId)?.name || ""}
                initialDetails={planDetails}
                onClose={() => setShowPlanModal(false)}
                onSave={(v) => {
                  setAppointmentsDurationsId(v.appointmentsDurationsId);
                  setAppointmentsSequenceId(v.appointmentsSequenceId);
                  setSelectedProtocols(v.protocols);
                  setDurationManuallySet(true);
                  setPlanConfigured(true);
                  // Collect section-2 plan details for the POST request
                  setPlanDetails({
                    treatmentPlanName: v.treatmentPlanName,
                    roleId: v.roleId,
                    genderId: v.genderId,
                    migzarId: v.migzarId,
                    treatmentRoomId: v.treatmentRoomId,
                    fundingSourcesId: v.fundingSourcesId,
                    treatmentTypesId: v.treatmentTypesId,
                  });
                }}
              />
            )}
            </>
          )}

          {/* Therapist Field - Now visible for both types */}
          <div className={styles.formGroup} ref={therapistDropdownRef}>
            <label className={styles.label}>
              <span className={styles.labelIcon}>👨‍⚕️</span>
              {t("APPOINTMENTS_THERAPIST")}
            </label>
            <div className={styles.customSelect}>
              <div
                className={`${styles.customSelectTrigger} ${showTherapistDropdown ? styles.customSelectActive : ''}`}
                onClick={() => setShowTherapistDropdown(!showTherapistDropdown)}
              >
                <span className={styles.customSelectValue}>
                  {therapistId ? `👨‍⚕️ ${selectedTherapistName}` : t("SELECT_THERAPIST")}
                </span>
                <span className={styles.customSelectArrow}>▼</span>
              </div>
              {showTherapistDropdown && (
                <div className={styles.customSelectDropdown}>
                  <div className={styles.searchContainer}>
                    <input
                      type="text"
                      className={styles.input}
                      style={{ minHeight: '36px', padding: '8px 12px', fontSize: '14px' }}
                      placeholder={t("SEARCH_PLACEHOLDER")}
                      value={therapistSearch}
                      onChange={(e) => setTherapistSearch(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                    />
                  </div>
                  <div className={styles.dropdownOptions}>
                    {appointmentType === "treatment" && therapistId > 0 && (
                      <div
                        className={styles.dropdownOption}
                        onClick={() => {
                          setTherapistId(0);
                          setTherapistGuid("");
                          setAppointmentDevice(null);
                          setStaffDevices([]);
                          setShowTherapistDropdown(false);
                        }}
                        style={{ borderBottom: "1px solid #e5e7eb" }}
                      >
                        <span className={styles.optionText} style={{ color: "#6b7280", fontStyle: "italic" }}>
                          ✕ {t("CLEAN_THE_SELECTION") || t("CLEAR") || "נקה בחירה"}
                        </span>
                      </div>
                    )}
                    {patientId && isFetchingLinked ? (
                      <div className={styles.dropdownOption} style={{ justifyContent: 'center', cursor: 'default' }}>
                        <span className={styles.optionText}>{t("LOADING")}...</span>
                      </div>
                    ) : (
                    therapistOptions.map((th) => (
                      <div
                        key={th.id}
                        className={`${styles.dropdownOption} ${therapistId === th.id ? styles.dropdownOptionSelected : ''}`}
                        onClick={() => handleTherapistSelect(th.id, th.guid || "")}
                      >
                        <span className={styles.optionText}>
                          {th.isLinked && <span style={{ marginInlineEnd: 6, color: '#22c55e' }}>✓</span>}
                          👨‍⚕️ {th.fullName}
                          {th.hasDevices && <span style={{ marginInlineStart: 6 }}>🔌</span>}
                        </span>
                        {therapistId === th.id && <span className={styles.checkmark}>✓</span>}
                      </div>
                    ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {therapistRequiresDevice && (
            <div className={styles.formGroup}>
              <label className={styles.label}>
                <span className={styles.labelIcon}>🔌</span>
                {t("DEVICE_SESSION")}
              </label>
              {appointmentDevice ? (
                <div className={styles.currentValueWrapper}>
                  <div className={styles.currentValue}>
                    {getDeviceSummaryLabel(appointmentDevice)}
                  </div>
                  <button
                    type="button"
                    className={styles.changeButton}
                    onClick={() => {
                      if (staffDevices.length > 0) {
                        setShowDeviceModal(true);
                      } else {
                        openDeviceConfiguration();
                      }
                    }}
                  >
                    {t("CHANGE")}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className={styles.changeButton}
                  onClick={() => openDeviceConfiguration()}
                  disabled={isFetchingDevices}
                >
                  {isFetchingDevices ? t("LOADING") : t("CONFIGURE_DEVICE")}
                </button>
              )}
            </div>
          )}

          {deviceError && (
            <div
              className={styles.warningsSection}
              style={{ borderColor: "#ef4444", backgroundColor: "#fef2f2" }}
            >
              <div className={`${styles.warningItem} ${styles.warningHigh}`}>
                <span className={styles.warningIcon}>⚠️</span>
                <span>{deviceError}</span>
              </div>
            </div>
          )}

          {appointmentType === 'treatment' && (
            /* Media Field */
            <div className={styles.formGroup} ref={mediaDropdownRef}>
              <label className={styles.label}>
                <span className={styles.labelIcon}>📍</span>
                {t("APPOINTMENTS_MEDIA")}
              </label>
              <div className={styles.customSelect}>
                <div
                  className={`${styles.customSelectTrigger} ${showMediaDropdown ? styles.customSelectActive : ''}`}
                  onClick={() => setShowMediaDropdown(!showMediaDropdown)}
                >
                  <span className={styles.customSelectValue}>
                    {mediaTypes.find(m => m.id === mediaId)?.name || t("SELECT_MEDIA")}
                  </span>
                  <span className={styles.customSelectArrow}>▼</span>
                </div>
                {showMediaDropdown && (
                  <div className={styles.customSelectDropdown}>
                    <div className={styles.dropdownOptions}>
                      {mediaTypes.map((media) => (
                        <div
                          key={media.id}
                          className={`${styles.dropdownOption} ${mediaId === media.id ? styles.dropdownOptionSelected : ''}`}
                          onClick={() => {
                            setMediaId(media.id);
                            setShowMediaDropdown(false);
                          }}
                        >
                          <span className={styles.optionText}>{media.name}</span>
                          {mediaId === media.id && <span className={styles.checkmark}>✓</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className={styles.formGroup}>
            <label className={styles.label}>
              <span className={styles.labelIcon}>📝</span>
              {appointmentType === 'treatment' ? t("APPOINTMENTS_NOTES") : (t("MESSAGE_CONTENT") || "תוכן ההודעה")}
            </label>
            <textarea
              className={styles.textarea}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              rows={3}
              placeholder={t("APPOINTMENTS_NOTES_PLACEHOLDER")}
            />
          </div>

          {/* Association prompt - therapist/patient not linked, with link button */}
          {appointmentType === 'treatment' && patientId && therapistId && !isPatientTherapistLinked && (
            <div
              className={styles.warningsSection}
              style={{ borderColor: "#f59e0b", backgroundColor: "#fffbeb" }}
            >
              <div className={styles.warningItem} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                <span className={styles.warningIcon}>ℹ️</span>
                <span>{t("APPOINTMENTS_THERAPIST_PATIENT_NOT_LINKED_PROMPT")}</span>
                <button
                  type="button"
                  onClick={handleLinkTherapistPatient}
                  disabled={isLinking}
                  style={{
                    padding: '6px 12px',
                    borderRadius: 6,
                    border: 'none',
                    background: '#8b5cf6',
                    color: 'white',
                    fontWeight: 600,
                    cursor: isLinking ? 'not-allowed' : 'pointer',
                    opacity: isLinking ? 0.7 : 1,
                  }}
                >
                  {isLinking ? t("LOADING") + "..." : t("APPOINTMENTS_LINK")}
                </button>
              </div>
            </div>
          )}

          {/* Association Error - from backend when create fails */}
          {associationError && (
            <div
              className={styles.warningsSection}
              style={{ borderColor: "#ef4444", backgroundColor: "#fef2f2" }}
            >
              <div className={styles.warningsTitle} style={{ color: "#dc2626" }}>
                <span>❌</span>
                {t("APPOINTMENTS_WARNINGS")}
              </div>
              <div className={`${styles.warningItem} ${styles.warningHigh}`}>
                <span className={styles.warningIcon}>⚠️</span>
                <span>{associationError}</span>
              </div>
            </div>
          )}

          {/* Warnings Section */}
          {conflicts.length > 0 && (
            <div className={styles.warningsSection}>
              <div className={styles.warningsTitle}>
                <span>⚠️</span>
                {t("APPOINTMENTS_WARNINGS")}
              </div>
              <div className={styles.warningsList}>
                {conflicts.map((conflict, index) => (
                  <div
                    key={index}
                    className={`${styles.warningItem} ${conflict.severity === 'warning' ? styles.warningHigh : styles.warningLow}`}
                  >
                    <span className={styles.warningIcon}>
                      {conflict.severity === 'warning' ? '⚠️' : 'ℹ️'}
                    </span>
                    <span>{conflict.message}</span>
                    {conflict.type === 'therapist_not_available' && therapistAvailability.length > 0 && (
                      <div className={styles.availabilityTooltip}>
                        <span className={styles.availabilityLabel}>📅 {t("THERAPIST_AVAILABILITY")}:</span>
                        <div className={styles.availabilityList}>
                          {therapistAvailability.map((av, idx) => (
                            <div key={idx} className={styles.availabilityItem}>
                              {getDayName(av.dayId)}: {av.startTime} - {av.endTime}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className={styles.modalFooter}>
          <button className={styles.cancelBtn} onClick={onClose}>{t("APPOINTMENTS_CLOSE")}</button>
          <button
            className={styles.saveBtn}
            onClick={handleCreate}
            disabled={
              isCreating ||
              !isStartBeforeEnd ||
              (appointmentType === 'treatment' &&
                (!patientId || !therapistId || !isPatientTherapistLinked || !seriesSettingsValid || !protocolsValid ||
                  (therapistRequiresDevice && !appointmentDevice))) ||
              (appointmentType === 'other' && !therapistId) ||
              isValidating
            }
          >
            {isCreating ? t("SAVING") : t("APPOINTMENTS_SAVE")}
          </button>
        </div>
      </div>

      {showDeviceModal && staffDevices.length > 0 && (
        <DeviceConfigurationModal
          devices={staffDevices}
          initialDevice={appointmentDevice}
          onConfirm={(device) => {
            setAppointmentDevice(device);
            setShowDeviceModal(false);
            setDeviceError("");
          }}
          onCancel={() => {
            setShowDeviceModal(false);
            if (!appointmentDevice) {
              setTherapistId(0);
              setTherapistGuid("");
            }
          }}
        />
      )}
    </div>
  );
};

export default CreateAppointmentModal;
